import { gsap }          from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';

const PARALLAX_SELECTOR = '[data-parallax]';
const CLASS_HIDDEN      = 'is-hidden';
const CLASS_VISIBLE     = 'is-visible';
const ROOT_MARGIN       = '-5% 0px -5% 0px';

const isClient        = typeof window !== 'undefined';
const isGsapAvailable = Boolean(gsap) && isClient;

if (isGsapAvailable && typeof gsap.registerPlugin === 'function') {
    gsap.registerPlugin(ScrollTrigger);
}

const parallaxAnimations = new WeakMap();

const initializeParallaxEffect = ({ element }) => {
    if (!isGsapAvailable || parallaxAnimations.has(element)) {
        return;
    }

    const contentElements = Array.from(element.querySelectorAll('[data-parallax-content]'));

    if (!contentElements.length) {
        parallaxAnimations.set(element, []);
        return;
    }

    try {
        const animations = contentElements.map((contentElement) => {
            const speed         = parseFloat(contentElement.getAttribute('data-parallax-speed')) || 0.5;
            const direction     = contentElement.getAttribute('data-parallax-direction') || 'up'; // 'up', 'down', 'both'
            const startPosition = parseFloat(contentElement.getAttribute('data-parallax-start')) || 0; // Начальное смещение в %
            const endPosition   = parseFloat(contentElement.getAttribute('data-parallax-end')) || 100; // Конечное смещение в %

            let yStart;
            let yEnd;

            switch (direction) {
            case 'down':
                yStart = `${startPosition}%`;
                yEnd   = `${endPosition * speed}%`;
                break;
            case 'both':
                yStart = `${-startPosition * speed}%`;
                yEnd   = `${endPosition * speed}%`;
                break;
            default:
                yStart = `${startPosition + (50 * speed)}%`;
                yEnd   = `${-endPosition * speed}%`;
            }

            console.log(`Параллакс: старт ${yStart}, конец ${yEnd}`);

            const animation = gsap.fromTo(
                contentElement,
                {
                    y : yStart
                },
                {
                    y        : yEnd,
                    ease     : 'none',
                    duration : 1,
                    paused   : true
                }
            );

            const scrollTrigger = ScrollTrigger.create({
                trigger             : element,
                start               : contentElement.getAttribute('data-scroll-start') || 'top bottom',
                end                 : contentElement.getAttribute('data-scroll-end') || 'bottom top',
                scrub               : parseFloat(contentElement.getAttribute('data-scrub')) || 1,
                animation,
                invalidateOnRefresh : true
            });

            return {
                animation,
                scrollTrigger,
                target : contentElement
            };
        });

        parallaxAnimations.set(element, animations);
    } catch (error) {
        console.error('initializeParallaxEffect failed.', error);
    }
};

const applyVisibilityState = ({
    element,
    isVisible
}) => {
    if (isVisible) {
        element.classList.add(CLASS_VISIBLE);
        element.classList.remove(CLASS_HIDDEN);
        initializeParallaxEffect({ element });
    } else {
        element.classList.add(CLASS_HIDDEN);
        element.classList.remove(CLASS_VISIBLE);

        const animations = parallaxAnimations.get(element);
        if (animations && animations.length) {
            animations.forEach(({ animation }) => {
                animation.pause(0);
            });
        }
    }
};

export default function parallaxObserver() {
    try {
        if (typeof window === 'undefined') {
            return {
                observer : null,
                elements : []
            };
        }

        const elements = Array.from(document.querySelectorAll(PARALLAX_SELECTOR));

        if (!elements.length) {
            return {
                observer : null,
                elements : []
            };
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                applyVisibilityState({
                    element   : entry.target,
                    isVisible : entry.isIntersecting
                });
            });
        }, {
            root       : null,
            threshold  : 0,
            rootMargin : ROOT_MARGIN
        });

        elements.forEach((element) => {
            applyVisibilityState({
                element,
                isVisible : false
            });
            observer.observe(element);
        });

        return {
            observer,
            elements
        };
    } catch (error) {
        console.error('initParallaxObserver failed.', error);
        return {
            observer : null,
            elements : []
        };
    }
}
